import type {
  ParticipantsDropdownValue,
  ParticipantsRoom,
} from '@lmn/common-search-widget/components/ParticipantsDropdown/services/types';
import type { PeopleData } from '@/app/components/search/utils';

/**
 * Adapts a `ParticipantsDropdownValue` object to a `PeopleData` array.
 *
 * This function serves as a reverse adapter, converting the Common Search Widget's
 * `ParticipantsDropdownValue` model back to the application's `PeopleData` model.
 * It transforms room and participant information from the Common Search Widget format
 * to the format used by the application.
 *
 * @param participantsValue - A `ParticipantsDropdownValue` object containing room
 *                            and participant information.
 * @returns An array of `PeopleData` objects compatible with the application's model.
 *
 * @remarks
 * - Each room in the input `participantsValue` is converted to an element in the output array.
 * - The `adultsCount` is directly mapped to `adults`.
 * - Children's ages are extracted from the `children` array and flattened into the `ages` array.
 *
 * @example
 * const participantsValue = {
 *   rooms: [
 *     { adultsCount: 2, children: [{ age: 5 }, { age: 7 }] },
 *     { adultsCount: 1, children: [] }
 *   ]
 * };
 * const peopleData = getPeopleDataFromCSW(participantsValue);
 * // Returns: [
 * //   { adults: 2, ages: [5, 7] },
 * //   { adults: 1, ages: [] }
 * // ]
 */

const getTSXPeopleData = (participants: ParticipantsDropdownValue): PeopleData =>
  participants?.rooms.map((room: ParticipantsRoom) => ({
    adults: room.adultsCount,
    ages: room.children.map((child) => child.age),
  }));

export default getTSXPeopleData;
